import { useEffect, useState } from 'react';
import type { Screen } from '@hub/shared';
import { DEPARTMENT_LABEL, navFor } from '@hub/shared';
import { useMe, rightsFor } from './lib/session.js';
import { useRoute } from './lib/router.js';
import { api } from './lib/api.js';
import { SEND_MODE_LABEL } from './lib/labels.js';
import { envFromApp } from './lib/environments.js';
import { dateTime } from './lib/format.js';
import { Login } from './screens/Login.js';
import { MyOverview } from './screens/MyOverview.js';
import { Races } from './screens/Races.js';
import { Customers } from './screens/Customers.js';
import { Trips } from './screens/Trips.js';
import { Communication } from './screens/Communication.js';
import { ComposeBroadcast } from './screens/ComposeBroadcast.js';
import { Reviews } from './screens/Reviews.js';
import { Exceptions } from './screens/Exceptions.js';
import { Revenue } from './screens/Revenue.js';
import { DataQuality } from './screens/DataQuality.js';
import { Jobs } from './screens/Jobs.js';
import { Settings } from './screens/Settings.js';
import { Simulator } from './screens/Simulator.js';
import { Care } from './screens/Care.js';
import { Transactional } from './screens/Transactional.js';
import { WebBehavior } from './screens/WebBehavior.js';
import { Deliverability } from './screens/Deliverability.js';
import { SecurityDialog } from './screens/SecurityDialog.js';
import { EnvironmentSwitcher } from './components/EnvironmentSwitcher.js';
import { BuildTag } from './components/BuildTag.js';
import { Icon, LoadingState } from './components/ui.js';
import type { IconName } from './components/ui.js';
import { applyTheme, currentTheme } from './lib/theme.js';
import type { Theme } from './lib/theme.js';

const SCREEN_TITLE: Record<Screen, string> = {
  overview: 'Můj přehled',
  races: 'Závody',
  customers: 'Zákazníci',
  trips: 'Cesty',
  communication: 'Komunikace',
  compose: 'Odeslat rozesílku',
  transactional: 'Transakční zprávy',
  reviews: 'Hodnocení',
  exceptions: 'Výjimky',
  revenue: 'Výnosy',
  data_quality: 'Kvalita dat a napojení',
  jobs: 'Úlohy',
  settings: 'Nastavení',
  simulator: 'Simulátor',
  care: 'Zákaznická péče',
  web_behavior: 'Chování na webu',
  deliverability: 'Doručitelnost',
};

/** Co obrazovka dělá; ukáže se jako nápověda u položky menu. */
const SCREEN_HINT: Record<Screen, string> = {
  overview: 'Čísla a fronta práce pro vaše oddělení',
  races: 'Kalendář závodů a jejich termíny',
  customers: 'Vyhledání zákazníka, jeho objednávky a zprávy',
  trips: 'Cesty zákazníků na závody',
  communication: 'Katalog zpráv, vypínače a historie odeslaného',
  compose: 'Průvodce: šablona, závod, komu poslat a kontrola před odesláním',
  transactional: 'Potvrzení objednávek a další zprávy z e-shopu',
  reviews: 'Dávky žádostí o hodnocení ke schválení',
  exceptions: 'Případy, které vyžadují lidské rozhodnutí',
  revenue: 'Kolik komunikace vydělala',
  data_quality: 'Zda jsou data a napojení v pořádku',
  jobs: 'Úlohy na pozadí: co běží a co se nepovedlo',
  settings: 'Režim odesílání, značky, přístupy, uživatelé',
  simulator: 'Zkouška na simulovaném čase (ne v Produkci)',
  care: 'Fronta zákaznické péče',
  web_behavior: 'Co lidé dělají na webu po e-mailu',
  deliverability: 'Odskoky, stížnosti, ověření domén a reputace',
};

const SCREEN_ICON: Record<Screen, IconName> = {
  overview: 'home',
  races: 'flag',
  customers: 'users',
  trips: 'route',
  communication: 'mail',
  compose: 'send',
  transactional: 'mail',
  reviews: 'star',
  exceptions: 'alert',
  revenue: 'chart',
  data_quality: 'database',
  jobs: 'refresh',
  settings: 'settings',
  simulator: 'flask',
  care: 'users',
  web_behavior: 'chart',
  deliverability: 'mail',
};

/** Názvy skupin menu pro lidi (oddělení z `navFor` plus „Další“). */
const GROUP_TITLE: Record<string, string> = {
  customer_care: 'Zákaznická péče',
  mailing: 'Mailing',
  obchod: 'Obchod',
  marketing: 'Marketing',
  admin: 'Správa systému',
  other: 'Další',
};

export function App(): JSX.Element {
  const me = useMe();
  const [route, go] = useRoute();
  const [securityOpen, setSecurityOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(currentTheme);

  /* Esc zavře rozbalené menu na mobilu. */
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const loggedIn = !!me.data;
  const current = route.screen;
  useEffect(() => {
    document.title = `${loggedIn ? SCREEN_TITLE[current] : 'Přihlášení'} · Marketing Hub`;
  }, [loggedIn, current]);

  if (me.isLoading) {
    return (
      <main style={{ padding: 32 }}>
        <LoadingState what="přihlášení" />
      </main>
    );
  }
  if (me.isError || !me.data) return <Login onDone={() => void me.refetch()} />;

  const rights = rightsFor(me.data.user.departments);
  const canSee = (s: Screen): boolean =>
    rights.read(s) &&
    !(me.data.hiddenScreens ?? []).includes(s) &&
    (s !== 'simulator' || me.data.appEnv !== 'production');
  const sections = navFor(me.data.user.departments, canSee);
  const visible: Screen[] = ['overview', ...sections.flatMap((x) => x.screens)];
  const screen: Screen = visible.includes(route.screen) ? route.screen : (visible[0] ?? 'overview');

  const mode = me.data.sendMode;
  const envKey = me.data.environment?.name ?? envFromApp(me.data.appEnv);
  const simulated = me.data.clockMode === 'simulated';
  const open = (s: Screen): void => {
    go(s);
    setMenuOpen(false);
  };

  return (
    <div className={`shell env-${envKey}`} data-env={envKey}>
      {/* Produkci poznáte na první pohled: barevný rám kolem celé aplikace. */}
      {envKey === 'production' ? (
        <div className="envframe" aria-hidden="true" data-testid="env-frame" />
      ) : null}
      <header className="topbar" data-testid="send-mode-banner">
        <button
          type="button"
          className="btn menubtn"
          aria-label={menuOpen ? 'Zavřít menu' : 'Otevřít menu'}
          aria-expanded={menuOpen}
          aria-controls="sidenav"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Icon name={menuOpen ? 'close' : 'menu'} />
        </button>
        <EnvironmentSwitcher me={me.data} />
        <BuildTag build={me.data.build} />
        <div className="tb-mode">
          <span className={`modechip ${mode}`} title="Režim odesílání zpráv">
            Odesílání: {SEND_MODE_LABEL[mode]}
          </span>
          <span className="tb-modetxt">
            {mode === 'dry_run'
              ? 'Engine běží celý, ale nic neodchází ven.'
              : mode === 'test_allowlist'
                ? 'Odchází jen na adresy z povoleného seznamu.'
                : 'Zprávy odcházejí zákazníkům.'}
          </span>
          {me.data.authDisabled ? (
            <span
              className="sample"
              title="Nastaveno AUTH_DISABLED. V produkci se hub s tímhle nespustí."
            >
              ⚠ přihlášení vypnuté (jen lokálně)
            </span>
          ) : null}
          {me.data.sendModeCeiling !== 'live' ? (
            <span className="sample tb-ceiling">
              prostředí dovoluje nejvýš {SEND_MODE_LABEL[me.data.sendModeCeiling]}
            </span>
          ) : null}
        </div>
        <span className={`clock${simulated ? ' sim' : ''}`} data-testid="clock">
          <Icon name="clock" size={14} />
          {simulated ? 'simulovaný čas ' : ''}
          {dateTime(me.data.now, me.data.defaultTimezone)}
        </span>
      </header>

      <div className="app">
        {menuOpen ? (
          <div className="navscrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />
        ) : null}
        <nav id="sidenav" className={`side${menuOpen ? ' open' : ''}`} aria-label="Hlavní menu">
          <div className="brand">
            <span className="logo" aria-hidden="true">
              <Icon name="route" size={18} />
            </span>
            <div className="brand-txt">
              <b>Marketing Hub</b>
              <small>
                {me.data.user.name} ·{' '}
                {me.data.user.departments.map((d) => DEPARTMENT_LABEL[d]).join(', ')}
              </small>
            </div>
          </div>
          <div className="navgroup">
            <button
              className={screen === 'overview' ? 'on' : ''}
              aria-current={screen === 'overview' ? 'page' : undefined}
              title={SCREEN_HINT.overview}
              onClick={() => open('overview')}
              data-testid="nav-overview"
            >
              <Icon name="home" />
              {SCREEN_TITLE.overview}
            </button>
          </div>
          {sections.map((section) => (
            <div className="navgroup" key={section.key} data-testid={`navsection-${section.key}`}>
              <span className="navhead">{GROUP_TITLE[section.key] ?? section.title}</span>
              {section.screens.map((s) => (
                <button
                  key={s}
                  className={screen === s ? 'on' : ''}
                  aria-current={screen === s ? 'page' : undefined}
                  title={SCREEN_HINT[s]}
                  onClick={() => open(s)}
                  data-testid={`nav-${s}`}
                >
                  <Icon name={SCREEN_ICON[s]} />
                  {SCREEN_TITLE[s]}
                </button>
              ))}
            </div>
          ))}
          <div className="foot">
            <button
              className="btn"
              data-testid="theme-toggle"
              aria-pressed={theme === 'dark'}
              onClick={() => {
                const next: Theme = theme === 'dark' ? 'light' : 'dark';
                applyTheme(next);
                setTheme(next);
              }}
            >
              {theme === 'dark' ? '☀ Světlý vzhled' : '☾ Tmavý vzhled'}
            </button>
            <button
              className="btn"
              data-testid="security-open"
              onClick={() => setSecurityOpen(true)}
            >
              Zabezpečení účtu
            </button>
            <button
              className="btn"
              onClick={async () => {
                await api.post('/api/auth/logout');
                window.location.reload();
              }}
            >
              Odhlásit se
            </button>
          </div>
        </nav>

        <SecurityDialog
          open={
            securityOpen || Boolean((me.data as { mfaSetupRequired?: boolean }).mfaSetupRequired)
          }
          forced={Boolean((me.data as { mfaSetupRequired?: boolean }).mfaSetupRequired)}
          onClose={() => setSecurityOpen(false)}
        />

        <main id="main" aria-label={SCREEN_TITLE[screen]}>
          {screen === 'overview' ? <MyOverview me={me.data} /> : null}
          {screen === 'races' ? <Races me={me.data} rights={rights} param={route.param} /> : null}
          {screen === 'customers' ? (
            <Customers me={me.data} rights={rights} param={route.param} />
          ) : null}
          {screen === 'trips' ? <Trips me={me.data} /> : null}
          {screen === 'communication' ? <Communication rights={rights} /> : null}
          {screen === 'compose' ? <ComposeBroadcast rights={rights} /> : null}
          {screen === 'transactional' ? (
            <Transactional rights={rights} param={route.param} />
          ) : null}
          {screen === 'reviews' ? <Reviews me={me.data} rights={rights} /> : null}
          {screen === 'exceptions' ? <Exceptions rights={rights} /> : null}
          {screen === 'revenue' ? <Revenue /> : null}
          {screen === 'data_quality' ? <DataQuality me={me.data} rights={rights} /> : null}
          {screen === 'jobs' ? <Jobs me={me.data} rights={rights} /> : null}
          {screen === 'settings' ? <Settings me={me.data} rights={rights} /> : null}
          {screen === 'simulator' ? <Simulator me={me.data} /> : null}
          {screen === 'care' ? <Care me={me.data} rights={rights} /> : null}
          {screen === 'deliverability' ? <Deliverability rights={rights} /> : null}
          {screen === 'web_behavior' ? <WebBehavior me={me.data} rights={rights} /> : null}
        </main>
      </div>
    </div>
  );
}
